import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Button,
  Avatar,
  Paper,
  LinearProgress,
  Fade,
  Zoom,
} from "@mui/material";
import {
  Groups,
  Engineering,
  LocalFireDepartment,
  Security,
  VerifiedUser,
  TrendingUp,
  EmojiEvents,
  Business,
  Public,
  Handshake,
  ArrowForward,
  Star,
  WorkspacePremium,
  Factory,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const HeroSection = styled(Box)`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: -200px;
    width: 400px;
    height: 400px;
    background: radial-gradient(
      circle,
      rgba(211, 47, 47, 0.1) 0%,
      transparent 70%
    );
  }
`;

const TimelineCard = styled(Card)`
  position: relative;
  padding: 24px;
  border-left: 4px solid #d32f2f;
  background: white;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const ValueCard = styled(Card)`
  height: 100%;
  background: white;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #d32f2f, #ff6b6b);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
    border-color: transparent;

    &::before {
      transform: scaleX(1);
    }
  }
`;

const StatCard = styled(Paper)`
  text-align: center;
  padding: 32px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
  }
`;

const About: React.FC = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    setVisible(true);
  }, []);

  const companyStats = [
    { label: t("aboutPage.stats.founded"), value: "2004", icon: <Business /> },
    {
      label: t("aboutPage.stats.experience"),
      value: "20+",
      icon: <EmojiEvents />,
    },
    { label: t("aboutPage.stats.employees"), value: "150+", icon: <Groups /> },
    {
      label: t("aboutPage.stats.completed"),
      value: "5000+",
      icon: <Engineering />,
    },
  ];

  const coreValues = [
    {
      title: t("aboutPage.values.quality.title"),
      description: t("aboutPage.values.quality.desc"),
      icon: <WorkspacePremium />,
      color: "#2196f3",
    },
    {
      title: t("aboutPage.values.safety.title"),
      description: t("aboutPage.values.safety.desc"),
      icon: <Security />,
      color: "#d32f2f",
    },
    {
      title: t("aboutPage.values.reliable.title"),
      description: t("aboutPage.values.reliable.desc"),
      icon: <Handshake />,
      color: "#4caf50",
    },
    {
      title: t("aboutPage.values.innovative.title"),
      description: t("aboutPage.values.innovative.desc"),
      icon: <TrendingUp />,
      color: "#ff9800",
    },
  ];

  const milestones = [
    {
      year: "2004",
      title: t("aboutPage.timeline.2004.title"),
      desc: t("aboutPage.timeline.2004.desc"),
    },
    {
      year: "2008",
      title: t("aboutPage.timeline.2008.title"),
      desc: t("aboutPage.timeline.2008.desc"),
    },
    {
      year: "2012",
      title: t("aboutPage.timeline.2012.title"),
      desc: t("aboutPage.timeline.2012.desc"),
    },
    {
      year: "2016",
      title: t("aboutPage.timeline.2016.title"),
      desc: t("aboutPage.timeline.2016.desc"),
    },
    {
      year: "2020",
      title: t("aboutPage.timeline.2020.title"),
      desc: t("aboutPage.timeline.2020.desc"),
    },
    {
      year: "2024",
      title: t("aboutPage.timeline.2024.title"),
      desc: t("aboutPage.timeline.2024.desc"),
    },
  ];

  const expertise = [
    { skill: "Yangın Kapısı Üretimi", level: 95 },
    { skill: "Proje Yönetimi", level: 90 },
    { skill: "Kalite Kontrol", level: 98 },
    { skill: "Müşteri Memnuniyeti", level: 99 },
    { skill: "Ar-Ge & İnovasyon", level: 85 },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Fade in={visible} timeout={1000}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography
                  variant="overline"
                  sx={{
                    color: "#ff6b6b",
                    fontWeight: 600,
                    letterSpacing: 2,
                    fontSize: "0.9rem",
                  }}
                >
                  {t("aboutPage.heroOverline")}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    mb: 3,
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {t("aboutPage.heroTitleLine1")}
                  <Box
                    component="span"
                    sx={{ color: "#ff6b6b", display: "block" }}
                  >
                    {t("aboutPage.heroTitleLine2")}
                  </Box>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    mb: 4,
                    fontWeight: 400,
                    lineHeight: 1.6,
                  }}
                >
                  {t("aboutPage.heroSubtitle")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  flexWrap="wrap"
                  sx={{ gap: 1 }}
                >
                  <Chip
                    icon={<Star />}
                    label={t("aboutPage.chips.tse")}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.1)",
                      color: "white",
                      border: "1px solid rgba(255,255,255,0.2)",
                    }}
                  />
                  <Chip
                    icon={<VerifiedUser />}
                    label={t("aboutPage.chips.ce")}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.1)",
                      color: "white",
                      border: "1px solid rgba(255,255,255,0.2)",
                    }}
                  />
                  <Chip
                    icon={<Public />}
                    label={t("aboutPage.chips.export")}
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.1)",
                      color: "white",
                      border: "1px solid rgba(255,255,255,0.2)",
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    justifyContent: { xs: "center", md: "flex-end" },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 200,
                      height: 200,
                      bgcolor: "rgba(255,255,255,0.1)",
                      border: "3px solid rgba(255,255,255,0.2)",
                    }}
                  >
                    <LocalFireDepartment
                      sx={{ fontSize: 100, color: "#ff6b6b" }}
                    />
                  </Avatar>
                </Box>
              </Grid>
            </Grid>
          </Fade>
        </Container>
      </HeroSection>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ mt: -6, position: "relative", zIndex: 1 }}>
        <Grid container spacing={3}>
          {companyStats.map((stat, index) => (
            <Grid key={index} item xs={6} md={3}>
              <Zoom
                in={visible}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <StatCard elevation={0}>
                  <Box sx={{ color: "#d32f2f", mb: 2 }}>{stat.icon}</Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      background: "linear-gradient(135deg, #d32f2f, #ff6b6b)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mb: 1,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {stat.label}
                  </Typography>
                </StatCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Company Story & Vision */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6}>
          {/* Company Story */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="overline"
              sx={{
                color: "#d32f2f",
                fontWeight: 600,
                letterSpacing: 2,
                fontSize: "0.9rem",
              }}
            >
              {t("aboutPage.storyOverline")}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 3 }}>
              {t("aboutPage.storyTitle")}
            </Typography>
            <Typography
              color="text.secondary"
              paragraph
              sx={{ lineHeight: 1.8, fontSize: "1.1rem" }}
            >
              {t("aboutPage.storyP1")}
            </Typography>
            <Typography
              color="text.secondary"
              paragraph
              sx={{ lineHeight: 1.8 }}
            >
              {t("aboutPage.storyP2")}
            </Typography>

            <Stack spacing={2} sx={{ mt: 4 }}>
              {expertise.map((item, index) => (
                <Box key={index}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {item.skill}
                    </Typography>
                    <Typography variant="body2" color="primary">
                      %{item.level}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={item.level}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#f0f0f0",
                      "& .MuiLinearProgress-bar": {
                        background: "linear-gradient(90deg, #d32f2f, #ff6b6b)",
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Timeline */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="overline"
              sx={{
                color: "#d32f2f",
                fontWeight: 600,
                letterSpacing: 2,
                fontSize: "0.9rem",
              }}
            >
              {t("aboutPage.timelineOverline")}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 3 }}>
              {t("aboutPage.timelineTitle")}
            </Typography>
            <Stack spacing={2}>
              {milestones.map((milestone, index) => (
                <Fade
                  key={index}
                  in={visible}
                  timeout={500}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <TimelineCard elevation={0}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 800,
                            color: "#d32f2f",
                          }}
                        >
                          {milestone.year}
                        </Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          {milestone.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {milestone.desc}
                        </Typography>
                      </Grid>
                    </Grid>
                  </TimelineCard>
                </Fade>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Core Values */}
      <Box sx={{ bgcolor: "#f8f9fa", py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                color: "#d32f2f",
                fontWeight: 600,
                letterSpacing: 2,
                fontSize: "0.9rem",
              }}
            >
              {t("aboutPage.valuesOverline")}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, mb: 2 }}>
              {t("aboutPage.valuesTitle")}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: "auto" }}
            >
              {t("aboutPage.valuesSubtitle")}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {coreValues.map((value, index) => (
              <Grid key={index} item xs={12} sm={6} md={3}>
                <Zoom
                  in={visible}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <ValueCard>
                    <CardContent sx={{ p: 4, textAlign: "center" }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: `${value.color}15`,
                          color: value.color,
                          mx: "auto",
                          mb: 3,
                        }}
                      >
                        {value.icon}
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        {value.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        {value.description}
                      </Typography>
                    </CardContent>
                  </ValueCard>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Mission & Vision */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 5,
                height: "100%",
                background: "linear-gradient(135deg, #d32f2f 0%, #ff6b6b 100%)",
                color: "white",
                borderRadius: 3,
              }}
            >
              <Factory sx={{ fontSize: 48, mb: 3 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
                {t("aboutPage.missionTitle")}
              </Typography>
              <Typography sx={{ lineHeight: 1.8, fontSize: "1.1rem" }}>
                {t("aboutPage.missionText")}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 5,
                height: "100%",
                background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                color: "white",
                borderRadius: 3,
              }}
            >
              <TrendingUp sx={{ fontSize: 48, mb: 3 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
                {t("aboutPage.visionTitle")}
              </Typography>
              <Typography sx={{ lineHeight: 1.8, fontSize: "1.1rem" }}>
                {t("aboutPage.visionText")}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
          py: 8,
          color: "white",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
                {t("aboutPage.ctaTitle")}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {t("aboutPage.ctaSubtitle")}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: { xs: "left", md: "right" } }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
              >
                <Button
                  variant="contained"
                  size="large"
                  component={RouterLink}
                  to="/products"
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: "white",
                    color: "#d32f2f",
                    px: 4,
                    py: 1.5,
                    "&:hover": {
                      bgcolor: "#f5f5f5",
                    },
                  }}
                >
                  {t("aboutPage.ctaButtons.products")}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={RouterLink}
                  to="/contact"
                  sx={{
                    borderColor: "white",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  {t("aboutPage.ctaButtons.contact")}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
